import React, { memo, useCallback } from "react";

import { Button } from "ui/components/Button/Button";

import { SubscriptionOverrideListProps } from "../../types";

const formatAdjustment = (type: string, value: number | null): string => {
  if (value === null) return "—";

  return type === "Absolute"
    ? `${value.toFixed(4).replace(".", ",")} €`
    : `${value > 0 ? "+" : ""}${value.toFixed(2).replace(".", ",")} %`;
};

const formatDate = (value: string): string =>
  new Date(value).toLocaleDateString("hr-HR");

/**
 * Posebni uvjeti uneseni u čarobnjaku.
 *
 * Drže se u stanju i šalju tek pri potpisu, u istoj transakciji — inače bi klijent nakratko
 * imao pretplatu bez uvjeta koji su mu obećani.
 */
const SubscriptionOverrideList: React.FC<SubscriptionOverrideListProps> = (props) => {
  const { overrides, keyNames, onAdd, onEdit, onRemove } = props;

  const handleEdit = useCallback(
    (keyCode: string) => () => {
      onEdit(keyCode);
    },
    [onEdit]
  );

  const handleRemove = useCallback(
    (keyCode: string) => () => {
      onRemove(keyCode);
    },
    [onRemove]
  );

  return (
    <div>
      <div className="flex items-start justify-between mb-1">
        <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
          Posebni uvjeti
        </h3>

        <Button variant="outlined" size="small" children="Dodaj" onClick={onAdd} />
      </div>

      <p className="text-xs text-slate-500 mb-3">
        Pojedinačne iznimke od cjenika i grupe. Spremaju se zajedno s pretplatom.
      </p>

      {overrides.length === 0 ? (
        <div className="border border-slate-200 px-4 py-3">
          <p className="text-sm text-slate-400">
            Nema posebnih uvjeta. Klijent plaća po cjeniku i grupi.
          </p>
        </div>
      ) : (
        <div className="border border-slate-200 divide-y divide-slate-100">
          {overrides.map((item) => (
            <div
              key={item.keyCode}
              className="flex items-center justify-between px-4 py-2.5 gap-4"
            >
              <div className="min-w-0">
                <p className="text-sm text-slate-800 font-medium">
                  {keyNames[item.keyCode] ?? item.keyCode}{" "}
                  <span className="text-slate-500 font-normal">
                    {formatAdjustment(item.adjustmentType, item.value)}
                  </span>
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {item.reason} · {formatDate(item.validFrom)} – {formatDate(item.validTo)}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outlined"
                  size="small"
                  children="Uredi"
                  onClick={handleEdit(item.keyCode)}
                />
                <Button
                  variant="outlined"
                  size="small"
                  children="Ukloni"
                  onClick={handleRemove(item.keyCode)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(SubscriptionOverrideList);
